import { useEffect, useState } from "react";
import {
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { ChevronDown, Check, type LucideIcon } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type PillSelectOption = {
  value: string;
  label: string;
  detail?: string;
  disabled?: boolean;
};

type Props = {
  value: string;
  options: PillSelectOption[];
  onChange: (value: string) => void;
  icon?: LucideIcon;
  placeholder?: string;
  displayValue?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  // When set, the search field is backed by an async provider: the (debounced)
  // trimmed query is handed to the parent, which updates `options` with the
  // results. Local substring filtering is skipped so server results show as-is.
  onSearch?: (query: string) => void;
  // Whether an async search is in flight (renders a "Searching" affordance).
  searching?: boolean;
  // Skip local substring filtering of `options` (default true keeps it on).
  filterOptionsLocally?: boolean;
  // Label shown when there are no options to display and nothing is loading.
  emptyLabel?: string;
  compact?: boolean;
  surface?: "pill" | "bar" | "field";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
};

export default function PillSelect({
  value,
  options,
  onChange,
  icon: Icon,
  placeholder = "Select",
  displayValue,
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search",
  onSearch,
  searching = false,
  filterOptionsLocally = true,
  emptyLabel = "No matches",
  compact = false,
  surface = "pill",
  open,
  onOpenChange,
  style,
  accessibilityLabel,
}: Props) {
  const capturePress = usePressCapture("PillSelect");
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((option) => option.value === value);
  const label = displayValue ?? selected?.label ?? placeholder;
  const isBarSurface = surface === "bar";
  const isFieldSurface = surface === "field";
  const isOpen = open ?? internalOpen;
  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLowerCase();
  const filteredOptions =
    normalizedQuery && filterOptionsLocally
      ? options.filter((option) =>
          [option.label, option.detail]
            .filter(Boolean)
            .some((text) => text!.toLowerCase().includes(normalizedQuery)),
        )
      : options;
  const setMenuOpen = (nextOpen: boolean) => {
    if (!nextOpen) setQuery("");
    if (onOpenChange) {
      onOpenChange(nextOpen);
      return;
    }
    setInternalOpen(nextOpen);
  };
  const handleSubmitEditing = () => {
    // Enter selects the first result rather than submitting free text: with an
    // async provider the visible options are already the resolved matches.
    const first = filteredOptions[0];
    if (onSearch && first && !first.disabled) {
      onChange(first.value);
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  // Debounce the query to the async provider so we search as the person types
  // without a request per keystroke.
  useEffect(() => {
    if (!onSearch || !isOpen) return;
    const handle = setTimeout(() => onSearch(trimmedQuery), 220);
    return () => clearTimeout(handle);
  }, [onSearch, isOpen, trimmedQuery]);

  return (
    <View
      style={[
        {
          position: "relative",
          zIndex: isOpen ? 1000 : 1,
          alignSelf: isFieldSurface ? "stretch" : "flex-start",
          maxWidth: "100%",
        },
        style,
      ]}
    >
      <Pressable
        disabled={disabled}
        accessibilityLabel={accessibilityLabel ?? placeholder}
        onPress={() => {
          capturePress(
            pressLabel(label, accessibilityLabel),
            isOpen ? "close_pill_select" : "open_pill_select",
          );
          setMenuOpen(!isOpen);
        }}
        style={{
          minHeight: isFieldSurface ? 37 : 36,
          ...(compact && !isFieldSurface ? { minHeight: 32 } : null),
          width: "100%",
          maxWidth: "100%",
          flexDirection: "row",
          alignItems: "center",
          gap: isFieldSurface ? 0 : compact ? 5 : 7,
          paddingLeft: isFieldSurface
            ? 12
            : compact
              ? Icon
                ? 10
                : 12
              : Icon
                ? 11
                : 14,
          paddingRight: isFieldSurface ? 8 : compact ? 8 : 9,
          paddingVertical: isFieldSurface ? 10 : compact ? 6 : 7,
          borderRadius: isFieldSurface ? 12 : 999,
          borderWidth: isFieldSurface ? 1 : 0,
          borderColor: isFieldSurface
            ? "rgba(42, 37, 34, 0.14)"
            : "transparent",
          backgroundColor: isFieldSurface
            ? "rgba(255,255,255,0.5)"
            : isBarSurface
              ? "transparent"
              : compact
                ? "rgba(42, 37, 34, 0.065)"
                : "rgba(42, 37, 34, 0.08)",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        {Icon ? (
          <Icon
            size={isFieldSurface ? 14 : compact ? 12 : 13}
            color={
              isBarSurface && isOpen
                ? EMBER.colors.text
                : EMBER.colors.textLight
            }
            strokeWidth={isBarSurface && isOpen ? 2 : 1.7}
          />
        ) : null}
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            flex: 1,
            flexShrink: 1,
            minWidth: 0,
            fontSize: compact && !isFieldSurface ? 12 : 13,
            fontWeight: isFieldSurface
              ? "400"
              : isBarSurface && isOpen
                ? "800"
                : "700",
            color:
              selected || displayValue || (isBarSurface && isOpen)
                ? EMBER.colors.text
                : EMBER.colors.textLight,
          }}
        >
          {label}
        </Text>
        <ChevronDown
          size={isFieldSurface ? 14 : compact ? 12 : 13}
          color={
            isBarSurface && isOpen ? EMBER.colors.text : EMBER.colors.textLight
          }
          strokeWidth={isBarSurface && isOpen ? 2 : 1.8}
          style={{
            marginLeft: "auto",
            transform: [{ rotate: isOpen ? "180deg" : "0deg" }],
          }}
        />
      </Pressable>

      {isOpen ? (
        <View
          style={{
            position: "absolute",
            top: compact ? 38 : 42,
            left: 0,
            width: 260,
            maxWidth: 320,
            borderRadius: 16,
            overflow: "hidden",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            borderWidth: 1,
            borderColor: "rgba(42, 37, 34, 0.08)",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.12,
            shadowRadius: 18,
            elevation: 9,
          }}
        >
          {searchable ? (
            <View
              style={{
                padding: 10,
                borderBottomWidth: 1,
                borderBottomColor: "rgba(42, 37, 34, 0.07)",
              }}
            >
              <TextInput
                autoFocus
                value={query}
                onChangeText={setQuery}
                onSubmitEditing={handleSubmitEditing}
                returnKeyType={onSearch ? "search" : "done"}
                placeholder={searchPlaceholder}
                placeholderTextColor={EMBER.colors.textFaint}
                style={{
                  minHeight: 34,
                  borderRadius: 999,
                  backgroundColor: "rgba(42, 37, 34, 0.06)",
                  paddingHorizontal: 12,
                  fontSize: 13,
                  color: EMBER.colors.text,
                }}
              />
            </View>
          ) : null}
          <ScrollView bounces={false} style={{ maxHeight: 320 }}>
            {filteredOptions.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <Pressable
                  key={option.value}
                  disabled={option.disabled}
                  onPress={() => {
                    capturePress(
                      pressLabel(option.label),
                      "pill_select_option",
                    );
                    onChange(option.value);
                    setMenuOpen(false);
                  }}
                  style={{
                    minHeight: 52,
                    paddingHorizontal: 14,
                    paddingVertical: 11,
                    borderTopWidth: index > 0 ? 1 : 0,
                    borderTopColor: "rgba(42, 37, 34, 0.07)",
                    backgroundColor: isSelected
                      ? "rgba(42, 37, 34, 0.06)"
                      : "transparent",
                    opacity: option.disabled ? 0.45 : 1,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <View style={{ flex: 1, gap: 2 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: isSelected ? "700" : "600",
                        color: EMBER.colors.text,
                      }}
                    >
                      {option.label}
                    </Text>
                    {option.detail ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          lineHeight: 17,
                          color: EMBER.colors.textLight,
                        }}
                      >
                        {option.detail}
                      </Text>
                    ) : null}
                  </View>
                  {isSelected ? (
                    <Check
                      size={15}
                      color={EMBER.colors.green}
                      strokeWidth={2.1}
                    />
                  ) : null}
                </Pressable>
              );
            })}
            {filteredOptions.length === 0 ? (
              <View style={{ paddingHorizontal: 14, paddingVertical: 18 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textLight,
                    textAlign: "center",
                  }}
                >
                  {searching
                    ? "Searching..."
                    : onSearch && trimmedQuery.length === 0
                      ? "Type to search"
                      : emptyLabel}
                </Text>
              </View>
            ) : null}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}
